import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { Group } from '../person/group';
import { PersonLogin, RoleName } from '../person/person-login';
import { Role } from '../person/role';
import { GoogleService } from './google-service';
import { OrganizationService } from './organization-service';
import { UserStateService } from './user-state.service';

@Service()
export class PersonLoginService {
  private readonly http = inject(HttpClient);
  private readonly googleService = inject(GoogleService);
  private readonly organizationService = inject(OrganizationService);
  private readonly router = inject(Router);
  private readonly userStateService = inject(UserStateService);


  private urlBase = environment.serviceUrl;
  private expirationKey = 'loginExpiration';
  private userKey = 'userLogin';
  private isSiblingOrgUserKey = 'siblingOrgUser';
  private applicationCredential: string | undefined;

  getAccessToken(): string {
    const personLogin = this.getCachedLogin();
    return personLogin?.token || '';
  }

  getCachedLoginWithoutRedirect(): PersonLogin | undefined {
    const personLogin = localStorage.getItem(this.userKey);
    if (!personLogin || this.isLoginExpired()) {
      return undefined;
    } else {
      return JSON.parse(personLogin) as PersonLogin;
    }
  }

  getCachedLogin(): PersonLogin | undefined {
    const personLogin = localStorage.getItem(this.userKey);
    if (!personLogin || this.isLoginExpired()) {
      localStorage.clear();
      this.userStateService.redirectUri = encodeURI(this.router.url);
      this.router.navigate(['/']);
      return undefined;
    } else {
      return JSON.parse(personLogin) as PersonLogin;
    }
  }

  isSiblingOrgUser(): boolean {
    const result = localStorage.getItem(this.isSiblingOrgUserKey);
    return result === 'true';
  }

  parseAuthCredential(credential: string): Observable<PersonLogin> {
    const subject = new Subject<PersonLogin>();
    const url = this.urlBase + 'personLogin/parseCredential/' + credential;
    this.http.get<PersonLogin>(url).subscribe(personLogin => {
      if (!personLogin) {
        subject.next(null!);
      } else {
        if (personLogin.person.active === false) {
          subject.error('Your account is inactive. Please contact Customer Service to have it reactivated');
        } else if (!personLogin.person.primaryOrgUnitId || personLogin.person.primaryOrgUnitId.length === 0) {
          subject.error('Please contact customer service to set a primary org for your account');
        } else {
          localStorage.setItem(GoogleService.TokenHeader, personLogin.token);
          this.organizationService.getOrganization(personLogin.person.primaryOrgUnitId).subscribe(result => {
            const primaryOrg = result;
            if (!primaryOrg || primaryOrg.active === false) {
              subject.error('Inactive primary org for your account');
            } else {
              const isSiblingOrgUser: boolean = this.organizationService.isSiblingOrg(result);
              localStorage.setItem(this.isSiblingOrgUserKey, isSiblingOrgUser.toString());
              const expirationDate: Date = new Date();
              expirationDate.setMonth(expirationDate.getMonth() + 1);
              localStorage.setItem(this.expirationKey, expirationDate.getTime().toString());
              localStorage.setItem(this.userKey, JSON.stringify(personLogin));
              subject.next(personLogin);
            }
          }, error => subject.error(error));
        }
      }
    }, error => subject.error(error));
    return subject.asObservable();
  }

  public logout(): void {
    localStorage.clear();
  }

  public hasRole(roleName: RoleName): boolean {
    const personLogin = this.getCachedLogin();
    if (!personLogin || !personLogin.roles || personLogin.roles.length === 0) {
      return false;
    } else {
      const role = personLogin.roles.filter(personRole => RoleName[personRole.name as keyof typeof RoleName] === roleName);
      return (role && role.length > 0);
    }
  }

  public isInternalOrRsm(): boolean {
    const personLogin = this.getCachedLogin();
    return !!(personLogin && (this.isInternalUser(personLogin.roles) || this.isRsmUser(personLogin.groups)));
  }

  isOutsideEngineer(): boolean {
    const personLogin = this.getCachedLogin();
    return !!(personLogin && personLogin.person && 'OUTENG' === personLogin.person.typeCode);
  }

  public isInternal(): boolean {
    const personLogin = this.getCachedLogin();
    return this.isInternalUser(personLogin!.roles);
  }

  private isInternalUser(roles: Role[]): boolean {
    return roles.length > 0;
  }

  public isRsm(): boolean {
    const personLogin = this.getCachedLogin();
    return this.isRsmUser(personLogin!.groups);
  }

  private isRsmUser(groups: Group[]): boolean {
    const rsmSuffixes: string[] = ['REP', 'OEM'];
    const rsmPrefixes: string[] = ['RST'];
    return groups.filter(group => this.hasPrefix(group.name, rsmPrefixes) || this.hasSuffix(group.name, rsmSuffixes)).length > 0;
  }

  private hasPrefix(groupName: string, prefixes: string[]): boolean {
    for (const prefix of prefixes) {
      if (groupName.lastIndexOf(prefix, 0) === 0) {
        return true;
      }
    }
    return false;
  }

  private hasSuffix(groupName: string, suffixes: string[]): boolean {
    for (const suffix of suffixes) {
      if (groupName.indexOf(suffix, groupName.length - suffix.length) > -1) {
        return true;
      }
    }
    return false;
  }

  isLoginExpired(): boolean {
    const expiration = localStorage.getItem(this.expirationKey);
    if (!expiration || isNaN(+expiration)) {
      return undefined!;
    }
    return new Date().getTime() > +expiration;
  }

  setApplicationCredential(credential: string): void {
    this.applicationCredential = credential;
  }

  getApplicationCredential(): string | undefined {
    return this.applicationCredential;
  }

  isAdmin(): boolean {
    return this.hasRole(RoleName.IECIT);
  }

  isPaperPilotUser(): boolean {
    return this.hasRole(RoleName.IECPaperPilotCreator) || this.isAdmin();
  }

  isBetaTester(): boolean {
    const personLogin = this.getCachedLogin();
    if (!personLogin || !personLogin.groups) {
      return false;
    } else {
      return personLogin.groups.filter(group => 'BETA_TEST' === group.name).length === 1;
    }
  }

  getPersonLogin(personId: string): Observable<PersonLogin> {
    const url = this.urlBase + 'personLogin/person/' + personId;
    return this.http.get<PersonLogin>(url, {headers: this.getRequestHeaders()});
  }

  savePersonLogin(personLogin: PersonLogin): Observable<PersonLogin> {
    const url = this.urlBase + 'personLogin/save';
    return this.http.post<PersonLogin>(url, JSON.stringify(personLogin), {headers: this.getPostHeaders()});
  }

  private getRequestHeaders(): HttpHeaders {
    return new HttpHeaders().set(GoogleService.TokenHeader, this.googleService.getAccessToken());
  }

  private getPostHeaders(contentType: string = 'application/json'): HttpHeaders {
    return this.getRequestHeaders().set("Content-Type", contentType);
  }

}
